import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Search, Compass, Check, Plus, Folder, GraduationCap, Code, Calculator, BookOpen, Users } from "lucide-react";

const folderIcons = { GraduationCap, Code, Calculator, BookOpen, Folder };

// Full-screen browsing for public collections other students share — the
// drawer only ever shows the folders you already have, this is where new
// ones are found.
const FolderDiscoverView = ({ token, onBack }) => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const [query, setQuery] = useState("");
  const [folders, setFolders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [joiningId, setJoiningId] = useState(null);
  const [error, setError] = useState(null);

  const fetchFolders = useCallback(async (search) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ search: search || "", limit: 24, page: 1 });
      const res = await fetch(`/api/folders/public?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Could not load collections.");
      const data = await res.json();
      setFolders(data.folders || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => fetchFolders(query), 300);
    return () => clearTimeout(timer);
  }, [query, fetchFolders]);

  const handleJoin = async (folder) => {
    setJoiningId(folder.id);
    setError(null);
    try {
      const res = await fetch("/api/folders/join", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ folderId: folder.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Could not join this collection.");
      setFolders((prev) => prev.map((f) => (f.id === folder.id ? { ...f, joined: true } : f)));
    } catch (err) {
      setError(err.message);
    } finally {
      setJoiningId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      className="min-h-screen"
    >
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-xl border-b border-border">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-4 flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-xl bg-muted hover:bg-muted/70 flex items-center justify-center transition-colors flex-shrink-0 border-none cursor-pointer"
            title="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="relative flex-1 max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search public collections..."
              className="w-full h-11 pl-11 pr-4 rounded-xl bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
          </div>
          <span className="text-xs text-muted-foreground hidden sm:block flex-shrink-0">{total} collections</span>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-8">
        <div className="flex items-center gap-2 mb-6">
          <Compass className="w-4 h-4 text-primary" />
          <h2 className="text-base font-bold text-foreground">Discover collections</h2>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-destructive/10 text-destructive text-xs font-medium">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
          </div>
        ) : folders.length === 0 ? (
          <div className="py-20 text-center border border-dashed border-border rounded-2xl">
            <p className="text-sm text-muted-foreground">
              {query.trim() ? "No public collection matches that search." : "No public collections have been shared yet."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {folders.map((folder) => {
              const Icon = folderIcons[folder.icon] || Folder;
              return (
                <motion.div
                  key={folder.id}
                  whileHover={{ y: -3 }}
                  className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-3 shadow-[0_4px_20px_rgba(0,0,0,0.03)]"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <button
                        onClick={() => navigate(`/${lang}/categories?id=${folder.id}`)}
                        className="text-sm font-bold text-foreground hover:text-primary transition-colors truncate block text-left bg-transparent border-none cursor-pointer p-0 w-full"
                      >
                        {folder.name}
                      </button>
                      <p className="text-[11px] text-muted-foreground truncate">
                        by {folder.ownerName}
                      </p>
                    </div>
                  </div>

                  <p className="text-[11px] text-muted-foreground line-clamp-2 min-h-[2rem]">
                    {folder.description || "No description provided."}
                  </p>

                  <div className="flex items-center justify-between pt-2 border-t border-border/50">
                    <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      <Users className="w-3 h-3" /> {folder.itemCount} items
                    </span>
                    {folder.joined ? (
                      <span className="flex items-center gap-1.5 text-[11px] font-bold text-primary">
                        <Check className="w-3.5 h-3.5" /> Joined
                      </span>
                    ) : (
                      <button
                        onClick={() => handleJoin(folder)}
                        disabled={joiningId === folder.id}
                        className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-primary text-primary-foreground text-[11px] font-bold hover:bg-primary/90 disabled:opacity-50 border-none cursor-pointer transition-all active:scale-95"
                      >
                        <Plus className="w-3.5 h-3.5" /> {joiningId === folder.id ? "Joining..." : "Join"}
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default FolderDiscoverView;
